import { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Target, ChevronDown, Loader2, Sparkles } from "lucide-react";
import SourceSelector from "@/components/sources/SourceSelector";
import { Button } from "@/components/ui/button";

const SAVE_DEBOUNCE_MS = 600;

export default function GoalContextPopover({ eventId, onSaved }) {
  const [goal, setGoal] = useState("");
  const [context, setContext] = useState("");
  const [outcomes, setOutcomes] = useState("");
  const [showMoreContext, setShowMoreContext] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!eventId) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(`${API}/agendas/${eventId}`, { withCredentials: true });
        if (cancelled) return;
        if (resp.data) {
          setGoal(resp.data.goal || "");
          setContext(resp.data.context || "");
          setOutcomes(resp.data.desired_outcomes || "");
          setShowMoreContext(!!(resp.data.context || resp.data.desired_outcomes));
          setItems(resp.data.items || []);
        }
      } catch {
        if (!cancelled) setGoal("");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [eventId]);

  const saveAgenda = useCallback(async () => {
    if (!eventId || !goal.trim()) return;
    setSaving(true);
    try {
      await axios.post(
        `${API}/agendas/${eventId}`,
        {
          goal: goal.trim(),
          context: context || null,
          desired_outcomes: outcomes || null,
        },
        { withCredentials: true }
      );
      onSaved?.({ goal, context, outcomes });
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.detail?.error === "meeting_limit") {
        toast.error(err.response.data.detail.message, {
          action: { label: "Upgrade", onClick: () => window.location.href = "/settings" },
          duration: 8000,
        });
      } else {
        toast.error("Failed to save");
      }
    } finally {
      setSaving(false);
    }
  }, [eventId, goal, context, outcomes, onSaved]);

  const generateAgenda = useCallback(async () => {
    if (!eventId || !goal.trim()) return;
    setGenerating(true);
    const payload = { goal: goal.trim(), context: context || null, desired_outcomes: outcomes || null };
    try {
      let resp;
      try {
        resp = await axios.post(
          `${API}/agendas/${eventId}/upsert-and-generate`,
          payload,
          { withCredentials: true }
        );
      } catch (e) {
        const isRouteNotFound = e.response?.status === 404 && (e.response?.data?.detail === "Not Found" || !e.response?.data?.detail);
        if (isRouteNotFound) {
          await axios.post(`${API}/agendas/${eventId}`, payload, { withCredentials: true });
          resp = await axios.post(`${API}/agendas/${eventId}/generate`, {}, { withCredentials: true });
        } else {
          throw e;
        }
      }
      const generatedItems = resp.data?.items || [];
      setItems(generatedItems);
      onSaved?.({ goal: goal.trim(), context, outcomes, items: generatedItems });
      if (generatedItems.length > 0) {
        toast.success(`Generated ${generatedItems.length} agenda items`);
      }
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.detail?.error === "meeting_limit") {
        toast.error(err.response.data.detail.message, {
          action: { label: "Upgrade", onClick: () => window.location.href = "/settings" },
          duration: 8000,
        });
      } else {
        const detail = err.response?.data?.detail;
        const msg = typeof detail === "string"
          ? detail
          : Array.isArray(detail) && detail[0]?.msg
            ? detail.map((e) => e.msg).join("; ")
            : detail?.message || err.message || "Failed to generate agenda";
        toast.error(msg);
      }
    } finally {
      setGenerating(false);
    }
  }, [eventId, goal, context, outcomes, onSaved]);

  useEffect(() => {
    if (!goal.trim()) return;
    const t = setTimeout(saveAgenda, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [goal, context, outcomes, saveAgenda]);

  if (loading) {
    return (
      <div className="w-[360px] p-4 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-sm">Loading…</span>
      </div>
    );
  }

  return (
    <div className="w-[360px] p-4 space-y-4" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center gap-2">
        <Target className="w-4 h-4 text-primary shrink-0" />
        <span className="text-sm font-medium">Goal & context</span>
        {saving && <span className="text-[10px] text-muted-foreground">Saving…</span>}
      </div>
      <div>
        <label className="text-[11px] text-muted-foreground font-medium mb-1 block">What do you want to achieve?</label>
        <input
          value={goal}
          onChange={(e) => setGoal(e.target.value)}
          className="surface-input w-full h-9 px-3 rounded-lg text-sm"
          placeholder="e.g. Align on Q2 priorities"
        />
      </div>
      {!showMoreContext ? (
        <button
          type="button"
          onClick={() => setShowMoreContext(true)}
          className="flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground"
        >
          <ChevronDown className="w-3 h-3" />
          Add more context
        </button>
      ) : (
        <div className="space-y-3">
          <div>
            <label className="text-[11px] text-muted-foreground font-medium mb-1 block">Additional context</label>
            <textarea
              value={context}
              onChange={(e) => setContext(e.target.value)}
              className="surface-input w-full px-3 py-2 rounded-lg text-xs resize-none"
              rows={2}
              placeholder="Background info for a better agenda"
            />
          </div>
          <div>
            <label className="text-[11px] text-muted-foreground font-medium mb-1 block">Desired outcomes</label>
            <textarea
              value={outcomes}
              onChange={(e) => setOutcomes(e.target.value)}
              className="surface-input w-full px-3 py-2 rounded-lg text-xs resize-none"
              rows={2}
              placeholder="What should be decided by the end?"
            />
          </div>
        </div>
      )}
      {goal.trim() && items.length === 0 && (
        <Button
          type="button"
          size="sm"
          className="w-full gap-2"
          onClick={generateAgenda}
          disabled={generating}
        >
          {generating ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Sparkles className="w-4 h-4" />
          )}
          {generating ? "Generating…" : "Generate agenda"}
        </Button>
      )}
      {items.length > 0 && (
        <p className="text-[11px] text-muted-foreground">
          {items.length} agenda item{items.length !== 1 ? "s" : ""} ready
        </p>
      )}
      <div className="pt-2 border-t border-border">
        <SourceSelector eventId={eventId} compact />
      </div>
    </div>
  );
}
